import React, { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  FormHelperText,
  TextField,
  Avatar,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import "react-phone-input-2/lib/bootstrap.css";

import PhoneInput from "react-phone-input-2";
import dayjs from "dayjs";
import { useAddUserMutation, useGetUsersByIdQuery, useUpdateUsersByIdMutation } from "../../services/user";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showError, showToast } from "../../constants/toasts";
import usePermission from "../../hooks/userAccess";

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const { isAdd } = usePermission({
    label: 'Manage Users',
    page_permission: 'isAdd',
  });

  const [AddUser] = useAddUserMutation();
  const [updateUser] = useUpdateUsersByIdMutation();

  const { data: userRes, isLoading: isUserLoading } = useGetUsersByIdQuery(id ? { id } : ({ id: '' } as any), { skip: !id });

  const isAddUser = location.pathname === "/manage-users/add";

  const [user, setUser] = useState<any>(null);
  const maxDob = dayjs().subtract(13, "year");

  // Update user state when API data is received
  useEffect(() => {
    if (userRes?.statusCode === 200 && userRes?.data && !isAddUser) {
      setUser(userRes.data);
    }
  }, [userRes, isAddUser]);

  const intitalValues = useMemo(() => {
    return {
      accountName: user?.accountName || "",
      firstName: user?.firstName || (user?.fullName ? (user?.fullName || "").split(" ")[0] : ""),
      lastName: user?.lastName || (user?.fullName ? (user?.fullName || "").split(" ").slice(1).join(" ") : ""),
      companyName: user?.companyName || "",
      fullName: user?.fullName || "",
      email: user?.email || "",
      phoneNo: user?.phoneNo || "",
      dialCode: user?.dialCode || "+971", // Default dial code set to UAE
      // memberAccount: user?.memberAccount || "",
      dateOfBirth: user?.dob ? dayjs(user?.dob) : null,
      nationality: user?.nationality || "",
      subscribeNewsletter: !!user?.subscribeNewsletter || false,
      agreeTerms: !!user?.agreeTerms || false,
      profileImage: null as any,
      profilePreview: user?.profileImage || "",
    };
  }, [user]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: intitalValues,
    validationSchema: Yup.object({
      accountName: Yup.string().nullable(),
      firstName: Yup.string().nullable(),
      lastName: Yup.string().nullable(),
      companyName: Yup.string().nullable(),
      fullName: Yup.string()
        .required("Full name is required")
        .min(3, "Full name must be at least 3 characters"),
      // memberAccount: Yup.string()
      //   .required("Member Account is required"),

      email: Yup.string()
        .required("Email is required")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        ),

      dialCode: Yup.string()
        .required("Dial code is required")
        .matches(/^\+?[0-9]+$/, "Enter a valid dial code"),

      phoneNo: Yup.string()
        .required("Phone number is required")
        .min(8, "Phone number must be at least 8 digits")
        .matches(/^[0-9]+$/, "Phone number must only contain digits"),

      dateOfBirth: Yup.date()
        .nullable()
        .required("Date of birth is required")
        .max(maxDob.toDate(), "User must be at least 13 years old"),

    }),
    onSubmit: async (values) => {
      const body: any = {
        fullName: values?.fullName?.trim() || `${(values?.firstName || "").trim()} ${(values?.lastName || "").trim()}`.trim(),
        accountName: values?.accountName,
        companyName: values?.companyName,
        firstName: values?.firstName,
        lastName: values?.lastName,
        email: values?.email,
        phoneNo: values?.phoneNo,
        dialCode: (values?.dialCode || "").startsWith("+")
          ? values.dialCode
          : `+${values?.dialCode || ""}`,
        dateOfBirth: values?.dateOfBirth ? dayjs(values.dateOfBirth).toISOString() : null,
        subscribeNewsletter: values?.subscribeNewsletter,
        agreeTerms: values?.agreeTerms,
        // nationality: "India",
        // memberAccount: values?.memberAccount,
      };
      console.log(body, "DATA");

      try {
        let response;
        if (id) {
          response = await updateUser({ body, id }).unwrap();
        } else {
          response = await AddUser(body).unwrap();
        }

        if (response?.statusCode === 200) {
          showToast(
            `User ${id ? 'updated' : 'added'} successfully`
          );
          navigate(-1);
        }
      } catch (err: any) {
        showError(err?.data?.message || "Something went wrong");
      }
    },
  });

  const handlePhoneChange = (phoneValue: any, country: any) => {
    const phoneWithoutCode = phoneValue?.replace(country.dialCode, "");
    formik.setFieldValue("phoneNo", phoneWithoutCode);
    const codeWithPlus = country?.dialCode?.toString().startsWith("+")
      ? country.dialCode
      : `+${country.dialCode}`;
    formik.setFieldValue("dialCode", codeWithPlus);
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handlePickImage = () => fileInputRef.current?.click();
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    formik.setFieldValue("profileImage", file);
    formik.setFieldValue("profilePreview", preview);
  };

  // Show loading state while fetching user data for edit mode
  if (!isAddUser && isUserLoading) {
    return (
      <div className="main_title">
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>{isAddUser ? "Add " : "Edit "} User</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - {" "}
          <button onClick={() => navigate("/manage-users")}>Manage Users</button> {" "}
          - {isAddUser ? "Add " : "Edit "} User
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p" style={{ alignItems: 'center', justifyContent: 'center' }}>
            <div className="control_group w_33" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar src={formik.values.profilePreview || ''} sx={{ width: 96, height: 96 }} />
              <IconButton onClick={handlePickImage} sx={{ mt: 1 }}>
                <img src="/static/images/edit2_icon.svg" alt="edit" />
              </IconButton>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
            </div>
          </div>
          <div className="gap_p">
            <div className="control_group w_33">
              <label htmlFor="accountName">Account Name</label>
              <TextField
                id="accountName"
                name="accountName"
                placeholder="Enter Account Name"
                value={formik.values.accountName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                hiddenLabel
                fullWidth
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="firstName">First Name</label>
              <TextField
                id="firstName"
                name="firstName"
                placeholder="First Name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                hiddenLabel
                fullWidth
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="lastName">Last Name</label>
              <TextField
                id="lastName"
                name="lastName"
                placeholder="Last Name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                hiddenLabel
                fullWidth
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="companyName">Company Name</label>
              <TextField
                id="companyName"
                name="companyName"
                placeholder="Enter Company Name"
                value={formik.values.companyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                hiddenLabel
                fullWidth
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="fullName">Full Name</label>
              <TextField name="fullName"
                id="fullName" placeholder="Name" value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                hiddenLabel
                helperText={
                  formik.touched.fullName &&
                  (formik.errors.fullName as any)
                }
                error={
                  formik.touched.fullName &&
                  Boolean(formik.errors.fullName)
                } fullWidth />
            </div>

            <div className="control_group w_33">
              <label htmlFor="email">Email</label>
              <TextField id="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.email && (formik.errors.email as any)
                }
                error={formik.touched.email && Boolean(formik.errors.email)}
                type="email"
                placeholder="Email"
                hiddenLabel
                fullWidth />
            </div>

            <div className="control_group w_33">
              <label htmlFor="phone">Phone Number</label>
              <PhoneInput
                country={"ae"}
                placeholder="Enter Phone Number"
                value={formik.values.dialCode + formik.values.phoneNo}
                onChange={(phoneNo, country) =>
                  handlePhoneChange(phoneNo, country)
                }
                inputStyle={{
                  width: "100%",
                  border:
                    formik.touched.phoneNo && formik.errors.phoneNo
                      ? "1px solid #d32f2f"
                      : "1px solid #ccc",
                }}
              />
              {formik.touched.phoneNo && formik.errors.phoneNo && (
                <FormHelperText error>
                  {formik.errors.phoneNo as any}
                </FormHelperText>
              )}
            </div>

            <div className="control_group w_33">
              <label htmlFor="dateOfBirth">DOB</label>
              <TextField
                variant="outlined"
                type="date"
                fullWidth
                hiddenLabel
                name="dateOfBirth"
                id="dateOfBirth"
                value={
                  formik.values.dateOfBirth
                    ? dayjs(formik.values.dateOfBirth).format("YYYY-MM-DD")
                    : ""
                }
                onChange={(e) =>
                  formik.setFieldValue("dateOfBirth", dayjs(e.target.value))
                }
                onBlur={formik.handleBlur}
                helperText={formik.touched.dateOfBirth && formik.errors.dateOfBirth}
                error={formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)}
                inputProps={{ max: maxDob.format("YYYY-MM-DD") }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>

            {/* <div className="control_group w_33">
              <label htmlFor="memberAccount">Member Account</label>
              <TextField
                id="memberAccount"
                name="memberAccount"
                placeholder="Member Account"
                value={formik.values.memberAccount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                hiddenLabel
                helperText={
                  formik.touched.memberAccount &&
                  (formik.errors.memberAccount as any)
                }
                error={
                  formik.touched.memberAccount &&
                  Boolean(formik.errors.memberAccount)
                }
                fullWidth
              />
            </div> */}
          </div>

          {isAdd && <div className="form_btn">
            <Button
              className="btn btn_primary"
              type="submit"
              disabled={formik.isSubmitting || (!isAddUser && isUserLoading)}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>}
        </form>
      </div>
    </>
  );
};

export default EditUser;